import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar } from "@/components/Avatar";
import { PeopleCheckList } from "@/components/PeopleCheckList";
import { DeptPicker } from "@/components/DeptPicker";
import { toast } from "sonner";
import { Loader2, Wrench, Plus, MessageSquare, Send, Users, Eye, Download } from "lucide-react";

const STATUS_META = {
  open: { label: "Open", cls: "bg-sky-500/15 text-sky-700" },
  in_progress: { label: "In progress", cls: "bg-amber-500/15 text-amber-700" },
  resolved: { label: "Resolved", cls: "bg-emerald-500/15 text-emerald-700" },
  closed: { label: "Closed", cls: "bg-muted text-muted-foreground" },
};
const PRIORITY_META = {
  low: { label: "Low", cls: "text-muted-foreground" },
  medium: { label: "Medium", cls: "text-amber-600" },
  high: { label: "High", cls: "text-destructive" },
};
const isClosed = (t) => t.status === "resolved" || t.status === "closed";
const downloadCsv = async (path, filename) => {
  try {
    const { data } = await api.get(path, { params: { tz_offset: -new Date().getTimezoneOffset() }, responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  } catch (e) { toast.error("Could not download the audit sheet"); }
};
const fmt = (iso) => { try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

function TicketCard({ t, onOpen }) {
  const sm = STATUS_META[t.status] || STATUS_META.open;
  const pm = PRIORITY_META[t.priority] || PRIORITY_META.medium;
  return (
    <button onClick={() => onOpen(t)} className="w-full text-left rounded-xl border border-border p-4 hover:border-primary/50 transition-colors" data-testid={`ticket-card-${t.id}`}>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-sm truncate">{t.title}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5 flex flex-wrap gap-x-2">
            <span>{t.department}</span><span>·</span><span>by {t.creator.name}</span><span>·</span><span>{fmt(t.created_at)}</span>
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`text-[10px] font-semibold ${pm.cls}`} data-testid={`ticket-priority-${t.id}`}>{pm.label}</span>
          <Badge className={`rounded-full border-0 text-[11px] ${sm.cls}`} data-testid={`ticket-status-${t.id}`}>{sm.label}</Badge>
        </div>
      </div>
      {t.assignees.length > 0 && (
        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
          <Users className="h-3 w-3 text-muted-foreground" />
          {t.assignees.map((a) => <span key={a.id} className="text-[11px] text-muted-foreground">{a.name}</span>).reduce((acc, el, i) => i ? [...acc, <span key={`c${i}`} className="text-[11px] text-muted-foreground">,</span>, el] : [el], [])}
        </div>
      )}
    </button>
  );
}

export default function TicketsView() {
  const { user } = useAuth();
  const [data, setData] = useState({ my_tickets: [], assigned_to_me: [], watching: [], can_manage: false });
  const [depts, setDepts] = useState([]);
  const [dir, setDir] = useState([]);
  const [loading, setLoading] = useState(true);
  const [raiseOpen, setRaiseOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", departments: [], priority: "medium", assignee_ids: [], watcher_ids: [] });
  const [active, setActive] = useState(null);
  const [comment, setComment] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, d, dr] = await Promise.all([api.get("/tickets"), api.get("/departments"), api.get("/directory")]);
      setData(t.data); setDepts(d.data); setDir(dr.data);
    } catch (e) { toast.error("Could not load tickets"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const raise = async (e) => {
    e.preventDefault();
    if (!form.departments.length) { toast.error("Pick at least one department"); return; }
    setSaving(true);
    try {
      await api.post("/tickets", form);
      toast.success("Ticket raised");
      setRaiseOpen(false);
      setForm({ title: "", description: "", departments: [], priority: "medium", assignee_ids: [], watcher_ids: [] });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to raise ticket"); }
    finally { setSaving(false); }
  };

  const patch = async (id, body, okMsg) => {
    try { await api.patch(`/tickets/${id}`, body); if (okMsg) toast.success(okMsg); await load(); setActive(null); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  const postComment = async () => {
    if (!comment.trim()) return;
    try {
      await api.post(`/tickets/${active.id}/comments`, { text: comment.trim() });
      setComment("");
      await load();
      const fresh = await api.get("/tickets");
      const all = [...fresh.data.my_tickets, ...fresh.data.assigned_to_me, ...fresh.data.watching];
      setActive(all.find((x) => x.id === active.id) || null);
      setData(fresh.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  const toggleId = (key) => (id) => setForm((f) => ({ ...f, [key]: f[key].includes(id) ? f[key].filter((x) => x !== id) : [...f[key], id] }));
  const allTickets = [...data.my_tickets, ...data.assigned_to_me, ...data.watching].filter((v, i, a) => a.findIndex((x) => x.id === v.id) === i);
  const myDept = user.department || "";
  const canManageActive = active && (user.is_owner || (!!myDept && (active.departments || []).includes(myDept)));
  const openOnly = (list) => list.filter((t) => !isClosed(t));
  const closedList = allTickets.filter(isClosed);
  const amAssignee = active && active.assignees.some((a) => a.id === user.id);

  const Section = ({ list, empty, testid }) => (
    list.length === 0 ? (
      <Card className="p-8 text-center rounded-xl" data-testid={`${testid}-empty`}><p className="text-sm text-muted-foreground">{empty}</p></Card>
    ) : (
      <div className="space-y-2" data-testid={testid}>{list.map((t) => <TicketCard key={t.id} t={t} onOpen={setActive} />)}</div>
    )
  );

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Wrench className="h-5 w-5" /></div>
        <div className="mr-auto">
          <h2 className="font-heading text-xl font-bold">Facility & IT</h2>
          <p className="text-sm text-muted-foreground">Raise a ticket to a department — assigned members and department members stay in the loop.</p>
        </div>
        <Button className="rounded-lg" onClick={() => setRaiseOpen(true)} data-testid="raise-ticket-btn"><Plus className="h-4 w-4 mr-1.5" /> Raise ticket</Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : (
        <Tabs defaultValue={data.assigned_to_me.length && !data.my_tickets.length ? "assigned" : "mine"} className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="mine" data-testid="tickets-tab-mine">My tickets <span className="ml-1 text-[10px] bg-secondary rounded-full px-1.5">{openOnly(data.my_tickets).length}</span></TabsTrigger>
            <TabsTrigger value="assigned" data-testid="tickets-tab-assigned">Assigned to me <span className="ml-1 text-[10px] bg-secondary rounded-full px-1.5">{openOnly(data.assigned_to_me).length}</span></TabsTrigger>
            <TabsTrigger value="watching" data-testid="tickets-tab-watching">Watching <span className="ml-1 text-[10px] bg-secondary rounded-full px-1.5">{openOnly(data.watching).length}</span></TabsTrigger>
            <TabsTrigger value="closed" data-testid="tickets-tab-closed">Closed / Resolved <span className="ml-1 text-[10px] bg-secondary rounded-full px-1.5">{closedList.length}</span></TabsTrigger>
          </TabsList>
          <TabsContent value="mine"><Section list={openOnly(data.my_tickets)} empty="No open tickets raised by you." testid="tickets-my-list" /></TabsContent>
          <TabsContent value="assigned"><Section list={openOnly(data.assigned_to_me)} empty="Nothing open assigned to you." testid="tickets-assigned-list" /></TabsContent>
          <TabsContent value="watching"><Section list={openOnly(data.watching)} empty="No open tickets visible to you here." testid="tickets-watching-list" /></TabsContent>
          <TabsContent value="closed">
            <div className="flex items-center justify-between gap-3 mb-3">
              <p className="text-xs text-muted-foreground">Resolved and closed tickets move here. Download the audit sheet to see who did what, and when.</p>
              {closedList.length > 0 && (
                <Button variant="outline" size="sm" className="rounded-lg shrink-0" onClick={() => downloadCsv("/tickets/export/closed.csv", "closed-tickets-audit.csv")} data-testid="export-closed-csv-btn">
                  <Download className="h-3.5 w-3.5 mr-1.5" /> Export all (CSV)
                </Button>
              )}
            </div>
            <Section list={closedList} empty="No resolved or closed tickets yet." testid="tickets-closed-list" />
          </TabsContent>
        </Tabs>
      )}

      {/* Raise ticket */}
      <Dialog open={raiseOpen} onOpenChange={setRaiseOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="raise-ticket-dialog">
          <DialogHeader>
            <DialogTitle>Raise a ticket</DialogTitle>
            <DialogDescription>Pick the department, add details, optionally assign members and tag watchers.</DialogDescription>
          </DialogHeader>
          <form onSubmit={raise} className="space-y-4">
            <div className="space-y-1.5"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required placeholder="e.g. AC not cooling in 3rd floor" data-testid="ticket-title-input" /></div>
            <div className="space-y-1.5"><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Describe the issue…" data-testid="ticket-description-input" /></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Departments (one or more)</Label>
                <DeptPicker value={form.departments} onChange={(v) => setForm({ ...form, departments: v })} testid="ticket-dept-picker" emptyHint="No departments yet — ask the Owner to add them in Team." />
              </div>
              <div className="space-y-1.5">
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger data-testid="ticket-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label>Assign to (optional, multiple allowed)</Label><PeopleCheckList people={dir} selected={form.assignee_ids} onToggle={toggleId("assignee_ids")} testid="ticket-assignees" max="max-h-32" /></div>
            <div className="space-y-1.5"><Label>Tag watchers (optional)</Label><PeopleCheckList people={dir.filter((p) => !form.assignee_ids.includes(p.id))} selected={form.watcher_ids} onToggle={toggleId("watcher_ids")} testid="ticket-watchers" max="max-h-32" /></div>
            <DialogFooter>
              <Button type="submit" className="rounded-lg w-full" disabled={saving} data-testid="ticket-raise-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Raise ticket"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Ticket detail */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="ticket-detail">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 flex-wrap pr-6">
                  {active.title}
                  <Badge className={`rounded-full border-0 text-[11px] ${(STATUS_META[active.status] || STATUS_META.open).cls}`}>{(STATUS_META[active.status] || STATUS_META.open).label}</Badge>
                  <span className={`text-[11px] font-semibold ${(PRIORITY_META[active.priority] || PRIORITY_META.medium).cls}`}>{(PRIORITY_META[active.priority] || PRIORITY_META.medium).label}</span>
                </DialogTitle>
                <DialogDescription>{active.department} · raised by {active.creator.name} · {fmt(active.created_at)}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {isClosed(active) && (
                  <Button variant="outline" size="sm" className="rounded-lg" onClick={() => downloadCsv(`/tickets/${active.id}/audit.csv`, `ticket-${active.id.slice(-6)}-audit.csv`)} data-testid="ticket-audit-csv-btn">
                    <Download className="h-3.5 w-3.5 mr-1.5" /> Download audit sheet (CSV)
                  </Button>
                )}
                {active.description && <p className="text-sm text-foreground/85 whitespace-pre-wrap break-words rounded-lg bg-secondary/40 p-3">{active.description}</p>}
                <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
                  <div className="flex items-center gap-2"><span className="text-muted-foreground text-xs flex items-center gap-1"><Users className="h-3.5 w-3.5" />Assigned:</span>
                    {active.assignees.length === 0 ? <span className="text-xs text-muted-foreground">—</span> : active.assignees.map((a) => <span key={a.id} className="inline-flex items-center gap-1"><Avatar src={a.avatar} name={a.name} className="h-5 w-5 text-[9px]" />{a.name}</span>)}
                  </div>
                  <div className="flex items-center gap-2"><span className="text-muted-foreground text-xs flex items-center gap-1"><Eye className="h-3.5 w-3.5" />Watching:</span>
                    {active.watchers.length === 0 ? <span className="text-xs text-muted-foreground">—</span> : active.watchers.map((w) => <span key={w.id} className="inline-flex items-center gap-1"><Avatar src={w.avatar} name={w.name} className="h-5 w-5 text-[9px]" />{w.name}</span>)}
                  </div>
                </div>

                {(canManageActive || amAssignee) && (
                  <div className="flex flex-wrap items-end gap-3 rounded-lg border border-border p-3" data-testid="ticket-manage-area">
                    <div className="space-y-1">
                      <Label className="text-xs">Status</Label>
                      <Select value={active.status} onValueChange={(v) => patch(active.id, { status: v }, "Status updated")}>
                        <SelectTrigger className="w-40 h-9" data-testid="ticket-status-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="open">Open</SelectItem>
                          <SelectItem value="in_progress">In progress</SelectItem>
                          <SelectItem value="resolved">Resolved</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {canManageActive && (
                      <>
                        <div className="flex-1 min-w-[220px] space-y-1">
                          <Label className="text-xs">Assign members</Label>
                          <PeopleCheckList people={dir} selected={active.assignees.map((a) => a.id)}
                            onToggle={(id) => {
                              const cur = active.assignees.map((a) => a.id);
                              const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
                              patch(active.id, { assignee_ids: next }, "Assignment updated");
                            }}
                            testid="ticket-detail-assignees" max="max-h-24" />
                        </div>
                        <div className="flex-1 min-w-[220px] space-y-1">
                          <Label className="text-xs">Tag watchers</Label>
                          <PeopleCheckList people={dir.filter((p) => !active.assignees.some((a) => a.id === p.id))} selected={active.watchers.map((w) => w.id)}
                            onToggle={(id) => {
                              const cur = active.watchers.map((w) => w.id);
                              const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
                              patch(active.id, { watcher_ids: next }, "Watchers updated");
                            }}
                            testid="ticket-detail-watchers" max="max-h-24" />
                        </div>
                      </>
                    )}
                  </div>
                )}

                <div>
                  <h4 className="font-heading font-semibold text-sm mb-2 flex items-center gap-1.5"><MessageSquare className="h-4 w-4" /> Comments ({active.comments.length})</h4>
                  <div className="space-y-2 max-h-56 overflow-auto" data-testid="ticket-comments">
                    {active.comments.length === 0 && <p className="text-xs text-muted-foreground">No comments yet.</p>}
                    {active.comments.map((c) => (
                      <div key={c.id} className="rounded-lg bg-secondary/40 p-2.5" data-testid={`ticket-comment-${c.id}`}>
                        <p className="text-[11px] text-muted-foreground mb-0.5">{c.by_name} · {fmt(c.at)}</p>
                        <p className="text-sm whitespace-pre-wrap break-words">{c.text}</p>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Write a comment…" data-testid="ticket-comment-input"
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); postComment(); } }} />
                    <Button className="rounded-lg shrink-0" onClick={postComment} disabled={!comment.trim()} data-testid="ticket-comment-btn"><Send className="h-4 w-4" /></Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
